import { NormalizedAlert } from '../types/alert';

const STORAGE_KEYS = {
  AUDIO_ENABLED: 'sn_audio_alerts_enabled',
  NOTIFIED_IDS: 'sn_notified_alert_ids',
  PERMISSION_STATE: 'sn_notification_permission',
};

export class NotificationService {
  private static audioCtx: AudioContext | null = null;
  private static memoryAudioEnabled: boolean = false;
  private static memoryNotifiedIds = new Set<string>();

  /**
   * Check if web notifications are supported in this browser
   */
  static isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /**
   * Get current notification permission
   */
  static getPermission(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  /**
   * Request browser notification permission
   */
  static async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) return false;
    try {
      const permission = await Notification.requestPermission();
      localStorage.setItem(STORAGE_KEYS.PERMISSION_STATE, permission);
      return permission === 'granted';
    } catch {
      return false;
    }
  }

  /**
   * Audio alerts state (stored in localStorage with memory fallback)
   */
  static isAudioEnabled(): boolean {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const item = localStorage.getItem(STORAGE_KEYS.AUDIO_ENABLED);
        if (item !== null) return item === 'true';
      } catch {
        // ignore
      }
    }
    return this.memoryAudioEnabled;
  }

  static setAudioEnabled(enabled: boolean): void {
    this.memoryAudioEnabled = enabled;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEYS.AUDIO_ENABLED, enabled ? 'true' : 'false');
      } catch {
        // ignore
      }
    }
    // If enabling, test-initialize AudioContext with a soft tone
    if (enabled) {
      this.playChime('info');
    }
  }

  /**
   * Web Audio API synthesized emergency chime.
   * Completely zero-cost, requires no external assets/audio files, and guaranteed low-latency.
   */
  static playChime(type: 'warning' | 'emergency' | 'info' = 'warning'): void {
    if (typeof window === 'undefined') return;
    try {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtxClass) return;

      if (!this.audioCtx || this.audioCtx.state === 'suspended') {
        this.audioCtx = new AudioCtxClass();
      }
      const ctx = this.audioCtx;

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === 'emergency') {
        // High-low two-tone European/US emergency tone
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(880, now); // A5
        osc.frequency.setValueAtTime(440, now + 0.15); // A4
        osc.frequency.setValueAtTime(880, now + 0.3); // A5
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
        osc.start(now);
        osc.stop(now + 0.5);
      } else if (type === 'warning') {
        // Double pulse beep
        osc.type = 'sine';
        osc.frequency.setValueAtTime(587.33, now); // D5
        osc.frequency.setValueAtTime(880, now + 0.12); // A5
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
        osc.start(now);
        osc.stop(now + 0.35);
      } else {
        // Gentle confirmation ding
        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, now); // C5
        osc.frequency.exponentialRampToValueAtTime(659.25, now + 0.1); // E5
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.25);
      }
    } catch (e) {
      console.warn('Audio synthesis unavailable or blocked by browser user-gesture policy:', e);
    }
  }

  /**
   * Retrieve list of previously notified alert IDs
   */
  private static getNotifiedIds(): Set<string> {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.NOTIFIED_IDS);
        if (stored) {
          return new Set(JSON.parse(stored));
        }
      } catch {
        // ignore
      }
    }
    return new Set(this.memoryNotifiedIds);
  }

  private static markAsNotified(alertId: string): void {
    this.memoryNotifiedIds.add(alertId);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const set = this.getNotifiedIds();
        set.add(alertId);
        // Keep only last 200 IDs to avoid storage bloat
        const arr = Array.from(set).slice(-200);
        localStorage.setItem(STORAGE_KEYS.NOTIFIED_IDS, JSON.stringify(arr));
      } catch {
        // ignore
      }
    }
  }

  /**
   * Scan active alerts on auto-sync and send alerts for new high-priority disruptions.
   * If savedStateCodes are provided, filters to match those states.
   */
  static processNewAlerts(
    alerts: NormalizedAlert[],
    savedStateCodes?: string[]
  ): { newlyNotifiedCount: number } {
    if (!this.isSupported()) return { newlyNotifiedCount: 0 };

    const notifiedSet = this.getNotifiedIds();
    const hasNotificationPerm = this.getPermission() === 'granted';
    const audioEnabled = this.isAudioEnabled();

    let count = 0;

    for (const alert of alerts) {
      // Only notify for Extreme or Severe events
      if (alert.severity !== 'Extreme' && alert.severity !== 'Severe') continue;

      // Deduplicate: check if already notified
      if (notifiedSet.has(alert.id)) continue;

      // If user specified saved locations, verify match
      if (savedStateCodes && savedStateCodes.length > 0) {
        const matchesLocation = savedStateCodes.some(
          (code) =>
            alert.areaDesc.toUpperCase().includes(` ${code}`) ||
            alert.areaDesc.toUpperCase().includes(`, ${code}`) ||
            alert.id.toUpperCase().includes(code)
        );
        if (!matchesLocation) continue;
      }

      // Mark as notified immediately
      this.markAsNotified(alert.id);
      count++;

      // Trigger Web Audio chime
      if (audioEnabled) {
        this.playChime(alert.severity === 'Extreme' ? 'emergency' : 'warning');
      }

      // Trigger Web Notification
      if (hasNotificationPerm) {
        try {
          const title = `🚨 [${alert.severity.toUpperCase()}] ${alert.event}`;
          const body = `${alert.areaDesc.slice(0, 120)}...\nSource: ${alert.senderName}`;
          new Notification(title, {
            body,
            icon: '/favicon.ico',
            tag: alert.id,
          });
        } catch (e) {
          console.warn('Unable to render desktop notification:', e);
        }
      }

      // Limit to 2 popups at once per sync cycle to avoid spamming the user
      if (count >= 2) break;
    }

    return { newlyNotifiedCount: count };
  }
}

